import { h } from 'preact'
import PropTypes from 'prop-types'
import { useMemo } from 'preact/hooks'

import classNames from 'lib/classNames'

import Icon from 'components/Icon'
import OrganizationIcon from 'components/OrganizationIcon'
import SingleLine from 'components/SingleLine'
import { ReactFlow, Handle } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import dagre from 'dagre'

import './index.sass'

const nodeWidth = 300
const nodeHeight = 120

function layoutGraph(members, relationships, customNodeWidth, customNodeHeight) {
  const nWidth = customNodeWidth || nodeWidth
  const nHeight = customNodeHeight || nodeHeight

  if (!members || members.length === 0) return { nodes: [], edges: [] }

  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))
  dagreGraph.setGraph({
    rankdir: 'TB',
    nodesep: 100,
    ranksep: 160,
    marginx: 40,
    marginy: 40,
  })

  const nodes = members.map(member => ({
    id: member.apikey,
    type: 'hierarchicalNode',
    data: { member },
    position: { x: 0, y: 0 },
    style: { width: nWidth },
  }))

  const edges = relationships.map(rel => ({
    id: `${rel.parentApikey}->${rel.childApikey}`,
    source: rel.parentApikey,
    target: rel.childApikey,
    type: 'smoothstep',
    animated: true,
    style: {
      strokeWidth: 2,
    },
  }))

  nodes.forEach(node =>
    dagreGraph.setNode(node.id, { width: nWidth, height: nHeight })
  )
  edges.forEach(edge =>
    dagreGraph.setEdge(edge.source, edge.target)
  )
  dagre.layout(dagreGraph)

  nodes.forEach(node => {
    const { x, y } = dagreGraph.node(node.id)
    node.position = { x: x - nWidth / 2, y: y - nHeight / 2 }
  })

  return { nodes, edges }
}

function HierarchicalNode({ data }) {
  const { member } = data

  const nodeClass = classNames('HierarchicalNetworkGraph-node', {
    'HierarchicalNetworkGraph-node--master': member.is_master_org,
    'HierarchicalNetworkGraph-node--sub': !member.is_master_org,
  })

  return <div className={nodeClass}>
    <Handle type="target" position="top" style={{ width: 0, height: 0, background: 'transparent', border: 'none' }} />
    <div className="HierarchicalNetworkGraph-node-header">
      <Icon type={member.is_master_org ? 'org' : 'subdirectory'} size="xs" />
      <span className="HierarchicalNetworkGraph-node-role">
        {member.is_master_org ? 'Prime Hub' : `Hub (Level ${member.depth})`}
      </span>
    </div>
    <div className="HierarchicalNetworkGraph-node-body">
      <OrganizationIcon organization={member} size="sm" bordered />
      <div className="HierarchicalNetworkGraph-node-name">
        <SingleLine>{member.name}</SingleLine>
      </div>
    </div>
    <Handle
      type="source" position="bottom"
      style={{ width: 0, height: 0, background: 'transparent', border: 'none' }}
    />
  </div>
}

const nodeTypes = { hierarchicalNode: HierarchicalNode }

export default function HierarchicalNetworkGraph({
  className,
  members,
  relationships,
  onNodeClick,
  nodeWidth: customNodeWidth,
  nodeHeight: customNodeHeight,
  ...props
}) {
  const { nodes, edges } = useMemo(
    () => layoutGraph(members, relationships, customNodeWidth, customNodeHeight),
    [members, relationships, customNodeWidth, customNodeHeight]
  )

  return <div
    className={classNames('HierarchicalNetworkGraph', { className })}
    {...props}
  >
    {nodes.length > 0
      ? <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        fitView
        proOptions={{ hideAttribution: true }}
        minZoom={0.3}
        maxZoom={1.5}
      />
      : <div className="HierarchicalNetworkGraph-empty">
        No hierarchy data available
      </div>
    }
  </div>
}

HierarchicalNetworkGraph.propTypes = {
  className: PropTypes.string,
  members: PropTypes.array.isRequired,
  relationships: PropTypes.array.isRequired,
  onNodeClick: PropTypes.func,
  nodeWidth: PropTypes.number,
  nodeHeight: PropTypes.number,
}
